import React, { useMemo, useState } from "react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Manual ISO parsing avoids `new Date("YYYY-MM-DD")`'s UTC-then-local-shift bug,
// which was rendering the wrong month (e.g. Dec 2025 instead of Jan 2026).
function parseISO(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m: m - 1, d };
}

function toISODate(y, m, d) {
  const mm = String(m + 1).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

/**
 * Props:
 *  - minDate, maxDate: "YYYY-MM-DD" strings bounding selectable days
 *  - value: currently selected "YYYY-MM-DD" (or null)
 *  - onChange: (isoDateString) => void
 */
export default function Calendar({ minDate, maxDate, value, onChange }) {
  const initial = useMemo(() => {
    if (value) return parseISO(value);
    if (minDate) return parseISO(minDate);
    const today = new Date();
    return { y: today.getFullYear(), m: today.getMonth(), d: today.getDate() };
  }, [value, minDate]);

  const [viewYear, setViewYear] = useState(initial.y);
  const [viewMonth, setViewMonth] = useState(initial.m);

  const min = minDate ? parseISO(minDate) : null;
  const max = maxDate ? parseISO(maxDate) : null;

  const cells = useMemo(() => {
    const firstOfMonth = new Date(viewYear, viewMonth, 1);
    const startWeekday = firstOfMonth.getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    const list = [];
    for (let i = 0; i < startWeekday; i++) list.push(null);
    for (let d = 1; d <= daysInMonth; d++) list.push(d);
    return list;
  }, [viewYear, viewMonth]);

  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  // Month-level bounds check, used to disable the prev/next arrows at the edges.
  const isPrevDisabled = () => {
    if (!min) return false;
    let m = viewMonth - 1, y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    return y < min.y || (y === min.y && m < min.m);
  };
  const isNextDisabled = () => {
    if (!max) return false;
    let m = viewMonth + 1, y = viewYear;
    if (m > 11) { m = 0; y += 1; }
    return y > max.y || (y === max.y && m > max.m);
  };

  const isDisabled = (day) => {
    if (!day) return true;
    const iso = toISODate(viewYear, viewMonth, day);
    if (minDate && iso < minDate) return true;
    if (maxDate && iso > maxDate) return true;
    return false;
  };

  const isSelected = (day) => {
    if (!day || !value) return false;
    return value === toISODate(viewYear, viewMonth, day);
  };

  return (
    <div className="oes-calendar">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <button
          type="button"
          className="btn btn-sm btn-light border-0"
          onClick={() => changeMonth(-1)}
          disabled={isPrevDisabled()}
          aria-label="Previous month"
        >
          <i className="bi bi-chevron-left" />
        </button>
        <span className="fw-semibold">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-light border-0"
          onClick={() => changeMonth(1)}
          disabled={isNextDisabled()}
          aria-label="Next month"
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>

      <div className="oes-calendar-grid oes-calendar-dow">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center small text-muted fw-semibold">
            {d}
          </div>
        ))}
      </div>

      <div className="oes-calendar-grid">
        {cells.map((day, idx) => {
          if (!day) return <div key={idx} className="oes-calendar-cell" />;
          const disabled = isDisabled(day);
          const selected = isSelected(day);
          return (
            <button
              type="button"
              key={idx}
              disabled={disabled}
              onClick={() => onChange(toISODate(viewYear, viewMonth, day))}
              className={
                "oes-calendar-cell btn btn-sm rounded-circle border-0 " +
                (selected
                  ? "btn-primary text-white fw-bold"
                  : disabled
                  ? "bg-transparent text-muted"
                  : "bg-transparent text-dark")
              }
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}